import { Backdrop, Fade, ListItem, Typography } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { Fragment, useCallback, useState } from 'react';
import type { MouseEvent, KeyboardEvent, FC } from 'react';

import { useConfiguration } from '../../contexts';
import type { OnLanguageChangeCallback } from '../../models';
import { LanguageSelectPopupTestIds } from '../../models';
import {
  ClearIcon,
  Content,
  Header,
  Root,
  StyledDivider,
  StyledList,
  StyledListItemButton,
  StyledListItemText,
} from './styles';

export interface LanguageSelectPopupProps {
  /**
   * Executes when user select new language
   */
  onLanguageChange: OnLanguageChangeCallback;
  /**
   * Controlled state for showing modal
   */
  isOpen: boolean;
  /**
   * If user wanted to close window by not changing language
   */
  onClose: () => void;
}

export const LanguageSelectModal: FC<LanguageSelectPopupProps> = (props) => {
  const { onLanguageChange, isOpen, onClose } = props;
  const { translations, languages } = useConfiguration();
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string | undefined>(undefined);
  const { getTestIdProps } = useTestId();

  const handleLanguageChange = useCallback<OnLanguageChangeCallback>(
    (language) => onLanguageChange(language),
    [onLanguageChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // If there are no languages yet then exit prematurely
      if (!languages) {
        return;
      }

      const selectedIndex = languages.findIndex((language) => language.code === selectedLanguageCode);
      let newSelectedIndex = selectedIndex;

      switch (event.code) {
        case 'ArrowDown': {
          newSelectedIndex = selectedIndex + 1 < languages.length ? selectedIndex + 1 : 0;
          break;
        }
        case 'ArrowUp': {
          newSelectedIndex = selectedIndex - 1 >= 0 ? selectedIndex - 1 : languages.length - 1;
          break;
        }
        case 'Enter': {
          const selectedLanguage = languages.find((language) => language.code === selectedLanguageCode);

          if (!selectedLanguage) {
            return;
          }

          handleLanguageChange(selectedLanguage);
          break;
        }
        default:
      }

      setSelectedLanguageCode(languages[newSelectedIndex]?.code);
    },
    [languages, handleLanguageChange, selectedLanguageCode],
  );

  const handleItemMouseEnter = useCallback((event: MouseEvent<HTMLLIElement>) => {
    const languageCode = event.currentTarget.dataset.langCode;

    setSelectedLanguageCode(languageCode);
  }, []);

  const handleListItemButtonClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // If there are no languages yet then exit prematurely
      if (!languages) {
        return;
      }

      const languageCode = event.currentTarget.dataset.langCode;
      const language = languages.find((availableLanguage) => availableLanguage.code === languageCode);

      if (!language) {
        return;
      }

      handleLanguageChange(language);
    },
    [languages, handleLanguageChange],
  );

  const handleItemMouseLeave = useCallback(() => {
    setSelectedLanguageCode(undefined);
  }, []);

  // We are probably still loading
  if (!languages || languages.length === 0) {
    return null;
  }

  return (
    <Root
      closeAfterTransition
      disableEnforceFocus
      disableRestoreFocus
      aria-describedby="transition-modal-description"
      aria-labelledby="transition-modal-title"
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(3px)',
          background: '#00000080 0% 0% no-repeat padding-box;',
        },
        timeout: 500,
      }}
      open={!!isOpen}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      {...getTestIdProps(LanguageSelectPopupTestIds.ROOT)}
    >
      <Fade in={isOpen}>
        <Content>
          <Header>
            <ClearIcon
              onClick={onClose}
              {...getTestIdProps(LanguageSelectPopupTestIds.CLOSE_ICON)}
            />
          </Header>
          <Typography
            variant="h4"
            component="h4"
            color="textPrimary"
            {...getTestIdProps(LanguageSelectPopupTestIds.TITLE)}
          >
            {translations.selectLanguage}
          </Typography>
          <StyledList {...getTestIdProps(LanguageSelectPopupTestIds.LIST)}>
            {languages.map((lang, index) => (
              <Fragment key={`item_${lang.code}`}>
                <ListItem
                  disablePadding
                  data-lang-code={lang.code}
                  aria-label={lang.name}
                  onMouseEnter={handleItemMouseEnter}
                  onMouseLeave={handleItemMouseLeave}
                >
                  <StyledListItemButton
                    {...getTestIdProps(`${LanguageSelectPopupTestIds.LIST_ITEM}_${lang.code}`)}
                    disableGutters
                    data-lang-code={lang.code}
                    selected={selectedLanguageCode === lang.code}
                    onClick={handleListItemButtonClick}
                  >
                    <StyledListItemText
                      disableTypography
                      primary={lang.name}
                    />
                  </StyledListItemButton>
                </ListItem>
                {/* Skip divider on last item */}
                {index < languages.length - 1 && (
                  <StyledDivider
                    key={`divider_${lang.code}`}
                    isItemHovered={selectedLanguageCode !== undefined}
                  />
                )}
              </Fragment>
            ))}
          </StyledList>
        </Content>
      </Fade>
    </Root>
  );
};
